import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Flag, Beaker, Scale, AlertTriangle, HelpCircle, Clock, CheckCircle, XCircle, Eye, ExternalLink } from 'lucide-react';
import Navbar from '@/components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons mapping
const CATEGORY_ICONS = {
  ingredient_issue: Beaker,
  macro_calorie_issue: Scale,
  portion_amount_issue: AlertTriangle,
  allergen_issue: AlertTriangle,
  other_issue: HelpCircle,
};

const CATEGORY_LABELS = {
  ingredient_issue: 'Ingredient',
  macro_calorie_issue: 'Macro/Calorie',
  portion_amount_issue: 'Portion',
  allergen_issue: 'Allergen',
  other_issue: 'Other',
};

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-yellow-100 text-yellow-800', icon: Flag },
  in_review: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Eye },
  fixed: { label: 'Fixed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  dismissed: { label: 'Dismissed', color: 'bg-gray-100 text-gray-600', icon: XCircle },
};

export default function RecipeReportsDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/recipe-reports/grouped`);
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (recipeId, newStatus) => {
    try {
      await axios.put(`${API}/recipe-reports/status/${recipeId}`, {
        status: newStatus
      });
      
      // Update local state
      setReports(prev => prev.map(r => 
        r.recipe_id === recipeId ? { ...r, status: newStatus } : r
      ));
      
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredReports = statusFilter === 'all' 
    ? reports 
    : reports.filter(r => r.status === statusFilter);

  // Count by status
  const statusCounts = {
    all: reports.length,
    new: reports.filter(r => r.status === 'new').length,
    in_review: reports.filter(r => r.status === 'in_review').length,
    fixed: reports.filter(r => r.status === 'fixed').length,
    dismissed: reports.filter(r => r.status === 'dismissed').length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Recipe Reports</h1>
            <p className="text-gray-500">{reports.length} recipes with reports</p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {Object.entries(statusCounts).map(([status, count]) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className="gap-2"
            >
              {status === 'all' ? 'All' : STATUS_CONFIG[status]?.label || status}
              <Badge variant="secondary" className="ml-1">{count}</Badge>
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Flag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No reports found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredReports.map((report) => {
              const StatusIcon = STATUS_CONFIG[report.status]?.icon || Flag;
              
              return (
                <Card key={report.recipe_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Recipe Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <button
                            onClick={() => navigate(`/coach/recipe-library?search=${encodeURIComponent(report.recipe_name)}`)}
                            className="font-semibold text-lg truncate text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            {report.recipe_name}
                            <ExternalLink className="h-4 w-4" />
                          </button>
                          <Badge className={STATUS_CONFIG[report.status]?.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_CONFIG[report.status]?.label}
                          </Badge>
                        </div>
                        
                        {/* Category Icons */}
                        <div className="flex items-center gap-4 mb-3">
                          {report.categories.map(cat => {
                            const Icon = CATEGORY_ICONS[cat] || HelpCircle;
                            return (
                              <div key={cat} className="flex items-center gap-1 text-sm text-gray-600">
                                <Icon className="h-4 w-4" />
                                <span>{CATEGORY_LABELS[cat] || cat}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Flag className="h-4 w-4" />
                            {report.report_count} report{report.report_count !== 1 ? 's' : ''}
                          </span>
                          {report.most_common_sub_issue && (
                            <span className="text-gray-400">•</span>
                          )}
                          {report.most_common_sub_issue && (
                            <span>Most reported: &quot;{report.most_common_sub_issue}&quot;</span>
                          )}
                          <span className="text-gray-400">•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {new Date(report.last_reported).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2">
                        {report.status !== 'fixed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateStatus(report.recipe_id, 'fixed')}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Solved
                          </Button>
                        )}
                        <Select
                          value={report.status}
                          onValueChange={(value) => updateStatus(report.recipe_id, value)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_review">In Review</SelectItem>
                            <SelectItem value="fixed">Fixed</SelectItem>
                            <SelectItem value="dismissed">Dismissed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
